import type { OperadorRole } from './operadoresAdmin';

export interface ModuloPermissao {
  id: string;
  nome: string;
  icone: string;
  path?: string;
  submodulos?: ModuloPermissao[];
}

export interface NivelAcessoConfig {
  id: string;
  empresaId: string;
  roleKey: OperadorRole | string;
  nome: string;
  descricao: string;
  cor: string; // tailwind bg class like 'bg-orange-100 text-orange-700'
  permissoes: string[]; // array of modulo ids that this role can access
  isDefault: boolean; // can't delete default levels
}

export const MODULOS_PERMISSOES: ModuloPermissao[] = [
  {
    id: 'dashboard',
    nome: 'Dashboard',
    icone: 'ri-dashboard-line',
    path: '/admin/incentivo/dashboard',
  },
  {
    id: 'participantes',
    nome: 'Participantes',
    icone: 'ri-group-line',
    path: '/admin/incentivo/participantes',
  },
  {
    id: 'campanhas',
    nome: 'Campanhas',
    icone: 'ri-megaphone-line',
    path: '/admin/incentivo/campanhas',
  },
  {
    id: 'simulador-ia',
    nome: 'Simulador IA',
    icone: 'ri-brain-line',
    path: '/admin/incentivo/simulador-ia',
  },
  {
    id: 'financeiro',
    nome: 'Financeiro',
    icone: 'ri-coin-line',
    path: '/admin/incentivo/pontuacao',
  },
  {
    id: 'recompensas',
    nome: 'Recompensas',
    icone: 'ri-gift-line',
    path: '/admin/incentivo/recompensas',
  },
  {
    id: 'auditoria',
    nome: 'Auditoria de Varejo',
    icone: 'ri-shield-check-line',
    submodulos: [
      { id: 'auditoria-dashboard', nome: 'Dashboard de Auditoria', icone: 'ri-dashboard-line', path: '/admin/auditoria/dashboard' },
      { id: 'auditoria-vendas', nome: 'Vendas para Auditoria', icone: 'ri-shopping-cart-line', path: '/admin/auditoria/vendas' },
      { id: 'auditoria-regras', nome: 'Regras de Auditoria', icone: 'ri-file-list-line', path: '/admin/auditoria/regras' },
      { id: 'auditoria-trilha', nome: 'Trilha de Auditoria', icone: 'ri-footprint-line', path: '/admin/auditoria/trilha' },
      { id: 'auditoria-relatorios', nome: 'Relatórios de Auditoria', icone: 'ri-bar-chart-line', path: '/admin/auditoria/relatorios' },
    ],
  },
  {
    id: 'gamificacao',
    nome: 'Gamificação',
    icone: 'ri-gamepad-line',
    submodulos: [
      { id: 'gamificacao-dashboard', nome: 'Dashboard de Gamificação', icone: 'ri-dashboard-line', path: '/admin/gamificacao/dashboard' },
      { id: 'gamificacao-missoes', nome: 'Missões', icone: 'ri-rocket-line', path: '/admin/gamificacao/missoes' },
      { id: 'gamificacao-regras-xp', nome: 'Regras de XP', icone: 'ri-award-line', path: '/admin/gamificacao/regras-xp' },
      { id: 'gamificacao-niveis', nome: 'Níveis', icone: 'ri-stack-line', path: '/admin/gamificacao/niveis' },
      { id: 'gamificacao-distintivos', nome: 'Distintivos', icone: 'ri-medal-line', path: '/admin/gamificacao/distintivos' },
      { id: 'gamificacao-jogos', nome: 'Jogos', icone: 'ri-gamepad-line', path: '/admin/gamificacao/jogos' },
      { id: 'gamificacao-logs', nome: 'Logs', icone: 'ri-file-text-line', path: '/admin/gamificacao/logs' },
      { id: 'gamificacao-analytics', nome: 'Analytics', icone: 'ri-line-chart-line', path: '/admin/gamificacao/analytics' },
    ],
  },
  {
    id: 'gestao-acesso',
    nome: 'Gestão de Acesso',
    icone: 'ri-shield-user-line',
    path: '/admin/gestao-acesso',
  },
  {
    id: 'niveis-acesso',
    nome: 'Níveis de Acesso',
    icone: 'ri-settings-3-line',
    path: '/admin/niveis-acesso',
  },
];

export function getTodosIdsModulos(): string[] {
  const ids: string[] = [];
  MODULOS_PERMISSOES.forEach((mod) => {
    if (mod.submodulos) {
      mod.submodulos.forEach((sub) => ids.push(sub.id));
    } else {
      ids.push(mod.id);
    }
  });
  return ids;
}

const niveisPadrao: Omit<NivelAcessoConfig, 'empresaId'>[] = [
  {
    id: 'default-admin',
    roleKey: 'admin_empresa',
    nome: 'Administrador',
    descricao: 'Acesso total — gerencia campanhas, finanças, operadores e configurações',
    cor: 'bg-orange-100 text-orange-700',
    permissoes: getTodosIdsModulos(),
    isDefault: true,
  },
  {
    id: 'default-gerente',
    roleKey: 'gerente',
    nome: 'Gerente',
    descricao: 'Gerencia campanhas, participantes, auditoria e visualiza relatórios',
    cor: 'bg-emerald-100 text-emerald-700',
    permissoes: [
      'dashboard', 'participantes', 'campanhas', 'simulador-ia', 'recompensas',
      'auditoria-dashboard', 'auditoria-vendas', 'auditoria-regras', 'auditoria-trilha', 'auditoria-relatorios',
      'gamificacao-dashboard', 'gamificacao-missoes', 'gamificacao-regras-xp', 'gamificacao-niveis',
      'gamificacao-distintivos', 'gamificacao-jogos', 'gamificacao-logs', 'gamificacao-analytics',
    ],
    isDefault: true,
  },
  {
    id: 'default-operador',
    roleKey: 'operador',
    nome: 'Operador',
    descricao: 'Opera campanhas, cadastra participantes e gerencia premiações',
    cor: 'bg-sky-100 text-sky-700',
    permissoes: [
      'dashboard', 'participantes', 'campanhas', 'recompensas',
      'auditoria-vendas', 'auditoria-regras',
      'gamificacao-missoes', 'gamificacao-jogos',
    ],
    isDefault: true,
  },
  {
    id: 'default-visualizador',
    roleKey: 'visualizador',
    nome: 'Visualizador',
    descricao: 'Apenas visualiza dashboards e relatórios',
    cor: 'bg-gray-100 text-gray-600',
    permissoes: [
      'dashboard',
      'auditoria-dashboard', 'auditoria-relatorios',
      'gamificacao-dashboard', 'gamificacao-analytics',
    ],
    isDefault: true,
  },
];

const STORAGE_KEY_PREFIX = 'admin_niveis_acesso_';

function getStorageKey(empresaId: string): string {
  return `${STORAGE_KEY_PREFIX}${empresaId}`;
}

export function getNiveisAcesso(empresaId: string): NivelAcessoConfig[] {
  try {
    const stored = localStorage.getItem(getStorageKey(empresaId));
    if (stored) {
      const customLevels = JSON.parse(stored) as NivelAcessoConfig[];
      const defaults = niveisPadrao.map((n) => ({ ...n, empresaId }));
      const merged = [...defaults];
      customLevels.forEach((cl) => {
        const existingIdx = merged.findIndex((m) => m.id === cl.id);
        if (existingIdx >= 0 && !merged[existingIdx].isDefault) {
          merged[existingIdx] = cl;
        } else if (existingIdx < 0) {
          merged.push(cl);
        }
      });
      return merged;
    }
  } catch {
    // ignore
  }
  return niveisPadrao.map((n) => ({ ...n, empresaId }));
}

export function salvarNiveisAcesso(empresaId: string, niveis: NivelAcessoConfig[]): void {
  const customLevels = niveis.filter((n) => !n.isDefault);
  localStorage.setItem(getStorageKey(empresaId), JSON.stringify(customLevels));
}

export function getNivelByRoleKey(empresaId: string, roleKey: string): NivelAcessoConfig | undefined {
  return getNiveisAcesso(empresaId).find((n) => n.roleKey === roleKey);
}

export function getPermissoesDoNivel(empresaId: string, roleKey: string): string[] {
  return getNivelByRoleKey(empresaId, roleKey)?.permissoes || [];
}

export function generateNivelId(): string {
  return `custom-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
}